/**
 * Tenant Detail Page
 *
 * Shows tenant details with tabs for Details, Settings, and Activity.
 * Supports editing tenant settings and status changes.
 * Capability-gated to users with admin:tenants capability.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  RefreshCw,
  Building2,
  Settings,
  Activity,
  MoreVertical,
  Archive,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  PauseCircle,
  Globe,
  BarChart2,
} from "lucide-react";
import type { ReactElement, ChangeEvent, FormEvent } from "react";
import { useState, useCallback, useMemo } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type {
  Tenant,
  TenantUpdateInput,
  TenantFeatures,
  TenantQuotas,
  TenantDetailTab,
  TenantStatus,
  ConfirmationAction,
} from "./types";
import { TENANT_STATUS_INFO, FEATURE_LABELS, FEATURE_DESCRIPTIONS } from "./types";

export function TenantDetailPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <TenantDetailContent />;
}

function TenantDetailContent(): ReactElement {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<TenantDetailTab>("details");
  const [isEditing, setIsEditing] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmationAction | null>(null);

  // Fetch tenant data
  const {
    data: tenant,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      const response = await apiRequest<Tenant>(`/api/v1/admin/tenants/${tenantId}`);
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: Boolean(tenantId),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (input: TenantUpdateInput) => {
      const response = await apiRequest<Tenant>(`/api/v1/admin/tenants/${tenantId}`, {
        method: "PATCH",
        body: input,
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setIsEditing(false);
    },
  });

  // Status change mutation
  const statusMutation = useMutation({
    mutationFn: async (newStatus: TenantStatus) => {
      const response = await apiRequest<Tenant>(`/api/v1/admin/tenants/${tenantId}`, {
        method: "PATCH",
        body: { status: newStatus },
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setConfirmAction(null);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest<void>(`/api/v1/admin/tenants/${tenantId}`, {
        method: "DELETE",
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
    },
    onSuccess: () => {
      navigate("/studio/admin/tenants");
    },
  });

  const handleConfirmAction = useCallback((): void => {
    if (!confirmAction) return;

    switch (confirmAction) {
      case "activate":
        statusMutation.mutate("active");
        break;
      case "suspend":
        statusMutation.mutate("suspended");
        break;
      case "archive":
        statusMutation.mutate("archived");
        break;
      case "delete":
        deleteMutation.mutate();
        break;
    }
  }, [confirmAction, statusMutation, deleteMutation]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load tenant"}
        onRetry={refetch}
      />
    );
  }

  if (!tenant) {
    return <NotFoundState />;
  }

  const statusInfo = TENANT_STATUS_INFO[tenant.status];

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to="/studio/admin/tenants"
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tenants
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-7 w-7" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{tenant.name}</h1>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    statusInfo.color === "success"
                      ? "bg-success/10 text-success"
                      : statusInfo.color === "warning"
                        ? "bg-warning/10 text-warning"
                        : "bg-foreground-muted/10 text-foreground-muted"
                  }`}
                >
                  {statusInfo.label}
                </span>
              </div>
              <p className="mt-1 text-foreground-muted">{tenant.slug}</p>
            </div>
          </div>

          {/* Actions dropdown */}
          <StatusActionsMenu status={tenant.status} onAction={setConfirmAction} />
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border">
        <nav className="flex gap-6">
          <TabButton
            active={activeTab === "details"}
            onClick={() => setActiveTab("details")}
            icon={<Building2 className="h-4 w-4" />}
            label="Details"
          />
          <TabButton
            active={activeTab === "settings"}
            onClick={() => setActiveTab("settings")}
            icon={<Settings className="h-4 w-4" />}
            label="Settings"
          />
          <TabButton
            active={activeTab === "activity"}
            onClick={() => setActiveTab("activity")}
            icon={<Activity className="h-4 w-4" />}
            label="Activity"
          />
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "details" && <DetailsTab tenant={tenant} />}
      {activeTab === "settings" && (
        <SettingsTab
          tenant={tenant}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
          onCancel={() => setIsEditing(false)}
          onSave={(input) => updateMutation.mutate(input)}
          isSaving={updateMutation.isPending}
          {...(updateMutation.error instanceof Error
            ? { error: updateMutation.error.message }
            : {})}
        />
      )}
      {activeTab === "activity" && <ActivityTab tenantId={tenant.id} />}

      {/* Confirmation dialog */}
      {confirmAction && (
        <ConfirmationDialog
          action={confirmAction}
          tenantName={tenant.name}
          isLoading={statusMutation.isPending || deleteMutation.isPending}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Tab Button Component
// ============================================================================

interface TabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: ReactElement;
  readonly label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps): ReactElement {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-foreground-muted hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ============================================================================
// Status Actions Menu
// ============================================================================

interface StatusActionsMenuProps {
  readonly status: TenantStatus;
  readonly onAction: (action: ConfirmationAction) => void;
}

function StatusActionsMenu({ status, onAction }: StatusActionsMenuProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="btn btn-secondary px-3">
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 min-w-48 rounded-md border border-border bg-background-secondary py-1 shadow-lg">
            {status !== "active" && (
              <button
                onClick={() => {
                  onAction("activate");
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-success hover:bg-background"
              >
                <CheckCircle className="h-4 w-4" />
                Activate
              </button>
            )}
            {status === "active" && (
              <button
                onClick={() => {
                  onAction("suspend");
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-warning hover:bg-background"
              >
                <PauseCircle className="h-4 w-4" />
                Suspend
              </button>
            )}
            {status !== "archived" && (
              <button
                onClick={() => {
                  onAction("archive");
                  setIsOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground-muted hover:bg-background"
              >
                <Archive className="h-4 w-4" />
                Archive
              </button>
            )}
            <div className="my-1 border-t border-border" />
            <button
              onClick={() => {
                onAction("delete");
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error hover:bg-background"
            >
              <Trash2 className="h-4 w-4" />
              Delete Permanently
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Details Tab
// ============================================================================

interface DetailsTabProps {
  readonly tenant: Tenant;
}

function DetailsTab({ tenant }: DetailsTabProps): ReactElement {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Basic Info */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
        <dl className="space-y-3">
          <div>
            <dt className="text-sm text-foreground-muted">Tenant ID</dt>
            <dd className="font-mono text-sm">{tenant.externalId}</dd>
          </div>
          <div>
            <dt className="text-sm text-foreground-muted">Name</dt>
            <dd>{tenant.name}</dd>
          </div>
          <div>
            <dt className="text-sm text-foreground-muted">Slug</dt>
            <dd className="font-mono">{tenant.slug}</dd>
          </div>
          <div>
            <dt className="text-sm text-foreground-muted">Created</dt>
            <dd>{new Date(tenant.createdAt).toLocaleString()}</dd>
          </div>
          <div>
            <dt className="text-sm text-foreground-muted">Last Updated</dt>
            <dd>{new Date(tenant.updatedAt).toLocaleString()}</dd>
          </div>
        </dl>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="space-y-2">
          <Link
            to={`/studio/admin/tenants/${tenant.id}/domains`}
            className="flex items-center gap-3 rounded-md border border-border px-4 py-3 hover:bg-background transition-colors"
          >
            <Globe className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Manage Domains</p>
              <p className="text-xs text-foreground-muted">
                Configure custom domain mappings for this tenant
              </p>
            </div>
          </Link>
          <Link
            to={`/studio/admin/tenants/${tenant.id}/usage`}
            className="flex items-center gap-3 rounded-md border border-border px-4 py-3 hover:bg-background transition-colors"
          >
            <BarChart2 className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium">View Usage</p>
              <p className="text-xs text-foreground-muted">
                Users, verticals, products, and recent activity
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Usage</h2>
        <dl className="space-y-3">
          <div className="flex items-center justify-between">
            <dt className="text-foreground-muted">Users</dt>
            <dd className="font-semibold">
              {tenant.usersCount ?? 0} / {tenant.settings.quotas.maxUsersPerTenant}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-foreground-muted">Verticals</dt>
            <dd className="font-semibold">
              {tenant.verticalsCount ?? 0} / {tenant.settings.quotas.maxVerticals}
            </dd>
          </div>
        </dl>
      </div>

      {/* Features Overview */}
      <div className="card lg:col-span-2">
        <h2 className="text-lg font-semibold mb-4">Features</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(Object.keys(tenant.settings.features) as (keyof TenantFeatures)[]).map((feature) => (
            <div
              key={feature}
              className={`flex items-center gap-2 rounded-md border px-3 py-2 ${
                tenant.settings.features[feature]
                  ? "border-success/30 bg-success/5"
                  : "border-border bg-background"
              }`}
            >
              {tenant.settings.features[feature] ? (
                <CheckCircle className="h-4 w-4 text-success" />
              ) : (
                <XCircle className="h-4 w-4 text-foreground-muted" />
              )}
              <span className="text-sm">{FEATURE_LABELS[feature]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Settings Tab
// ============================================================================

interface SettingsTabProps {
  readonly tenant: Tenant;
  readonly isEditing: boolean;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
  readonly onSave: (input: TenantUpdateInput) => void;
  readonly isSaving: boolean;
  readonly error?: string;
}

function SettingsTab({
  tenant,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  isSaving,
  error,
}: SettingsTabProps): ReactElement {
  const [formState, setFormState] = useState({
    name: tenant.name,
    features: { ...tenant.settings.features },
    quotas: { ...tenant.settings.quotas },
  });

  const handleFeatureChange = useCallback((feature: keyof TenantFeatures): void => {
    setFormState((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: !prev.features[feature],
      },
    }));
  }, []);

  const handleQuotaChange = useCallback((quota: keyof TenantQuotas, value: string): void => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setFormState((prev) => ({
        ...prev,
        quotas: {
          ...prev.quotas,
          [quota]: numValue,
        },
      }));
    }
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      onSave({
        name: formState.name,
        settings: {
          features: formState.features,
          quotas: formState.quotas,
        },
      });
    },
    [formState, onSave]
  );

  const hasChanges = useMemo(() => {
    return (
      formState.name !== tenant.name ||
      JSON.stringify(formState.features) !== JSON.stringify(tenant.settings.features) ||
      JSON.stringify(formState.quotas) !== JSON.stringify(tenant.settings.quotas)
    );
  }, [formState, tenant]);

  if (!isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex justify-end">
          <button onClick={onEdit} className="btn btn-primary">
            Edit Settings
          </button>
        </div>

        {/* Features */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Features</h2>
          <div className="space-y-3">
            {(Object.keys(tenant.settings.features) as (keyof TenantFeatures)[]).map((feature) => (
              <div key={feature} className="flex items-start gap-3">
                <div
                  className={`mt-0.5 h-4 w-4 rounded ${
                    tenant.settings.features[feature] ? "bg-success" : "bg-foreground-muted/30"
                  }`}
                />
                <div>
                  <p className="text-sm font-medium">{FEATURE_LABELS[feature]}</p>
                  <p className="text-xs text-foreground-muted">{FEATURE_DESCRIPTIONS[feature]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quotas */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Quotas</h2>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt className="text-sm text-foreground-muted">Max Verticals</dt>
              <dd className="text-lg font-semibold">{tenant.settings.quotas.maxVerticals}</dd>
            </div>
            <div>
              <dt className="text-sm text-foreground-muted">Max Products/Vertical</dt>
              <dd className="text-lg font-semibold">
                {tenant.settings.quotas.maxProductsPerVertical}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-foreground-muted">Max Users</dt>
              <dd className="text-lg font-semibold">{tenant.settings.quotas.maxUsersPerTenant}</dd>
            </div>
            <div>
              <dt className="text-sm text-foreground-muted">Max API Requests/Day</dt>
              <dd className="text-lg font-semibold">
                {tenant.settings.quotas.maxApiRequestsPerDay.toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-foreground-muted">Max Storage</dt>
              <dd className="text-lg font-semibold">{tenant.settings.quotas.maxStorageMb} MB</dd>
            </div>
          </dl>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Basic Settings</h2>
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-1">
            Tenant Name
          </label>
          <input
            id="name"
            type="text"
            value={formState.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFormState((prev) => ({ ...prev, name: e.target.value }))
            }
            className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Features */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Features</h2>
        <div className="space-y-3">
          {(Object.keys(formState.features) as (keyof TenantFeatures)[]).map((feature) => (
            <label key={feature} className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formState.features[feature]}
                onChange={() => handleFeatureChange(feature)}
                className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <div>
                <p className="text-sm font-medium">{FEATURE_LABELS[feature]}</p>
                <p className="text-xs text-foreground-muted">{FEATURE_DESCRIPTIONS[feature]}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Quotas */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Quotas</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label htmlFor="maxVerticals" className="block text-sm font-medium mb-1">
              Max Verticals
            </label>
            <input
              id="maxVerticals"
              type="number"
              min="1"
              value={formState.quotas.maxVerticals}
              onChange={(e) => handleQuotaChange("maxVerticals", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="maxProductsPerVertical" className="block text-sm font-medium mb-1">
              Max Products/Vertical
            </label>
            <input
              id="maxProductsPerVertical"
              type="number"
              min="1"
              value={formState.quotas.maxProductsPerVertical}
              onChange={(e) => handleQuotaChange("maxProductsPerVertical", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="maxUsersPerTenant" className="block text-sm font-medium mb-1">
              Max Users
            </label>
            <input
              id="maxUsersPerTenant"
              type="number"
              min="1"
              value={formState.quotas.maxUsersPerTenant}
              onChange={(e) => handleQuotaChange("maxUsersPerTenant", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="maxApiRequestsPerDay" className="block text-sm font-medium mb-1">
              Max API Requests/Day
            </label>
            <input
              id="maxApiRequestsPerDay"
              type="number"
              min="0"
              value={formState.quotas.maxApiRequestsPerDay}
              onChange={(e) => handleQuotaChange("maxApiRequestsPerDay", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="maxStorageMb" className="block text-sm font-medium mb-1">
              Max Storage (MB)
            </label>
            <input
              id="maxStorageMb"
              type="number"
              min="0"
              value={formState.quotas.maxStorageMb}
              onChange={(e) => handleQuotaChange("maxStorageMb", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-error bg-error/10 p-4">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" disabled={isSaving || !hasChanges} className="btn btn-primary">
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </form>
  );
}

// ============================================================================
// Activity Tab
// ============================================================================

interface ActivityTabProps {
  readonly tenantId: string;
}

function ActivityTab({ tenantId }: ActivityTabProps): ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ["tenant-activity", tenantId],
    queryFn: async () => {
      const response = await apiRequest<{ items: readonly AuditEntry[] }>(
        `/api/v1/admin/audit?tenantId=${tenantId}&limit=50`
      );
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-foreground-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data?.items.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
        <Activity className="h-8 w-8" />
        <p className="mt-2">No activity recorded yet</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
      <div className="space-y-4">
        {data.items.map((entry) => (
          <div key={entry.id} className="flex items-start gap-3 text-sm">
            <div className="mt-1 h-2 w-2 rounded-full bg-primary" />
            <div className="flex-1">
              <p>
                <span className="font-medium">{entry.actor}</span>{" "}
                <span className="text-foreground-muted">{entry.action}</span>{" "}
                {entry.target && <span className="font-mono text-xs">{entry.target}</span>}
              </p>
              <p className="text-xs text-foreground-muted">
                {new Date(entry.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface AuditEntry {
  readonly id: string;
  readonly action: string;
  readonly actor: string;
  readonly target?: string;
  readonly timestamp: string;
}

// ============================================================================
// Confirmation Dialog
// ============================================================================

interface ConfirmationDialogProps {
  readonly action: ConfirmationAction;
  readonly tenantName: string;
  readonly isLoading: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function ConfirmationDialog({
  action,
  tenantName,
  isLoading,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps): ReactElement {
  const config = {
    activate: {
      title: "Activate Tenant",
      message: `Are you sure you want to activate "${tenantName}"? This will restore all access.`,
      confirmLabel: "Activate",
      variant: "success" as const,
    },
    suspend: {
      title: "Suspend Tenant",
      message: `Are you sure you want to suspend "${tenantName}"? Users will lose access but data will be preserved.`,
      confirmLabel: "Suspend",
      variant: "warning" as const,
    },
    archive: {
      title: "Archive Tenant",
      message: `Are you sure you want to archive "${tenantName}"? This will disable all access and hide it from lists.`,
      confirmLabel: "Archive",
      variant: "warning" as const,
    },
    delete: {
      title: "Delete Tenant",
      message: `Are you sure you want to permanently delete "${tenantName}"? This action cannot be undone.`,
      confirmLabel: "Delete",
      variant: "error" as const,
    },
  }[action];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full ${
              config.variant === "error"
                ? "bg-error/10 text-error"
                : config.variant === "warning"
                  ? "bg-warning/10 text-warning"
                  : "bg-success/10 text-success"
            }`}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold">{config.title}</h2>
        </div>

        <p className="text-foreground-muted mb-6">{config.message}</p>

        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`btn ${
              config.variant === "error"
                ? "btn-error"
                : config.variant === "warning"
                  ? "btn-warning"
                  : "btn-success"
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              config.confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading tenant...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

function NotFoundState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground-muted/10">
        <Building2 className="h-6 w-6 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">Tenant not found</h3>
      <p className="mt-2 text-foreground-muted">
        The tenant you're looking for doesn't exist or has been deleted.
      </p>
      <Link to="/studio/admin/tenants" className="btn btn-primary mt-4">
        Back to Tenants
      </Link>
    </div>
  );
}
